import React, { useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function EditarImpuesto({
  initial,
  onClose = () => {},
  onSaved = () => {},
  embedded = false,
}) {
  
  // =======================================================
  // HELPER PARA PARSEAR FECHAS (ESPECÍFICO PARA TU BACKEND)
  // =======================================================
  const toDateValue = (val) => {
    if (!val) return "";

    // 1. Si ya viene listo (YYYY-MM-DD), lo devolvemos
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        return val;
    }

    // 2. Lógica para "19-ene-2026 00:00:00"
    if (typeof val === "string" && val.includes("-")) {
        try {
            // A. Quitamos la hora: "19-ene-2026 00:00:00" -> "19-ene-2026"
            const fechaSola = val.split(" ")[0].trim();
            
            // B. Separamos por guiones
            const partes = fechaSola.split("-"); // ["19", "ene", "2026"]
            
            if (partes.length === 3) {
                 const [dia, mesTexto, anio] = partes;
                 
                 // Mapa de meses (minúsculas, primeras 3 letras)
                 const meses = {
                    ene: "01", jan: "01",
                    feb: "02",
                    mar: "03",
                    abr: "04", apr: "04",
                    may: "05", mai: "05",
                    jun: "06",
                    jul: "07",
                    ago: "08", aug: "08",
                    set: "09", sep: "09",
                    oct: "10", out: "10",
                    nov: "11",
                    dic: "12", dec: "12"
                 };

                 const mesKey = mesTexto.toLowerCase().substring(0, 3);
                 const mesNumero = meses[mesKey];

                 if (mesNumero) {
                     // Retornar formato HTML5: YYYY-MM-DD
                     return `${anio}-${mesNumero}-${dia.padStart(2, '0')}`;
                 }
            }
        } catch (e) {
            console.warn("No se pudo parsear fecha española:", val);
        }
    }

    // 3. Fallback genérico
    try {
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime())) {
            return dateObj.toISOString().split("T")[0];
        }
    } catch (e) {}

    return "";
  };

  const id = useMemo(() => initial?.id ?? null, [initial]);
  
  const [numero, setNumero] = useState("");
  const [data, setData] = useState("");
  const [dataFechaini, setDataFechaini] = useState("");
  const [dataFechafin, setDataFechafin] = useState("");
  const [dataIngreso, setDataIngreso] = useState("");

  // =======================================================
  // CARGA DE DATOS
  // =======================================================
  useEffect(() => {
    if (initial) {
        // Asignación directa usando el helper
        setNumero(initial.numero || "");
        setData(toDateValue(initial.data));
        setDataFechaini(toDateValue(initial.dataFechaini));
        setDataFechafin(toDateValue(initial.dataFechafin));
        setDataIngreso(toDateValue(initial.dataIngreso));
    }
  }, [initial]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isValid = numero.trim() !== "";

  // ESC para cerrar solo si no es embedded
  useEffect(() => {
    if(embedded) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, embedded]);

  const changed = useMemo(() => {
    if (!initial) return false;
    // Comparamos contra el valor convertido para evitar falsos "cambios"
    return (
      numero !== (initial.numero || "") ||
      data !== toDateValue(initial.data) ||
      dataFechaini !== toDateValue(initial.dataFechaini) ||
      dataFechafin !== toDateValue(initial.dataFechafin) ||
      dataIngreso !== toDateValue(initial.dataIngreso)
    );
  }, [numero, data, dataFechaini, dataFechafin, dataIngreso, initial]);

  const handleSave = async () => {
    if (!id || !changed || !isValid || saving) return;
    
    setError("");
    setSaving(true);

    try {
      const payload = {
        numero: numero.trim(),
        data: data || null,
        dataFechaini: dataFechaini || null,
        dataFechafin: dataFechafin || null,
        dataIngreso: dataIngreso || null,
      };

      const updated = await appFetchJson(apiRoutes.certificados.updateUrl(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      onSaved(updated);
      
      if (!embedded) {
          onClose();
      }
    } catch (e) {
      console.error("Error editando certificado:", e);
      setError(e?.message || "Error al actualizar el certificado.");
    } finally {
      setSaving(false);
    }
  };

  // === RENDER ===
  const content = (
      <>
        {/* HEADER: Solo si NO es embedded */}
        {!embedded && (
            <div className="modal-header flex justify-between items-center">
                <h2 className="section-title">Editar Certificado</h2>
                <button className="modal-close-x" onClick={onClose}>
                <FaTimes />
                </button>
            </div>
        )}

        {/* BODY */}
        <div className={embedded ? "" : "modal-body"}>
            {!embedded && (
                <p className="text-gray-600 mb-4 italic">
                    Edite los datos del Certificado
                </p>
            )}

            {error && <div className="text-red-600 mb-3 font-medium">{error}</div>}

            <div className="flex flex-col gap-4">
                {/* 1. Fecha Ingreso (READONLY) */}
                <div className="w-full">
                    <label className="form-label">Fecha Ingreso al Sistema:</label>
                    <input
                        type="date"
                        className="input w-full bg-gray-200 text-gray-700 cursor-not-allowed"
                        value={dataIngreso}
                        readOnly 
                    />
                </div>

                {/* 2. Nro. Certificado */}
                <div className="w-full">
                    <label className="form-label">Nro. Certificado *</label>
                    <input
                        type="text"
                        className="input w-full"
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                    />
                </div>

                {/* 3. Fecha del Certificado */}
                <div className="w-full">
                    <label className="form-label">Fecha del Certificado:</label>
                    <input
                        type="date"
                        className="input w-full"
                        value={data}
                        onChange={(e) => setData(e.target.value)}
                    />
                </div>

                {/* 4. Fecha Inicio Vigencia */}
                <div className="w-full">
                    <label className="form-label">Fecha Inicio Vigencia:</label>
                    <input
                        type="date"
                        className="input w-full"
                        value={dataFechaini}
                        onChange={(e) => setDataFechaini(e.target.value)}
                    />
                </div>

                {/* 5. Fecha Fin Vigencia */}
                <div className="w-full">
                    <label className="form-label">Fecha Fin Vigencia:</label>
                    <input
                        type="date"
                        className="input w-full"
                        value={dataFechafin}
                        onChange={(e) => setDataFechafin(e.target.value)}
                    />
                </div>
            </div>
        </div>

        {/* FOOTER */}
        <div className={embedded ? "mt-6" : "modal-footer"}>
            <div className="btn-row">
                <BtnGuardar 
                    label="Guardar Cambios"
                    isValid={isValid && changed}
                    saving={saving}
                    onClick={handleSave}
                />
                {!embedded && <BtnCancelar onClick={onClose} />}
            </div>
        </div>
      </>
  );

  if (embedded) {
      return <div className="bg-white p-1">{content}</div>;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
}